/**
 * Tenant bootstrap — unauthenticated same-origin `GET /api/bootstrap`.
 *
 * Fetched with plain `fetch` (not the axios client): the client needs
 * `authOrigin` to resolve auth URLs, so routing this call through axios is
 * circular. The payload is stored in a module-scoped variable — never web
 * storage. Required fields are the saas-auth §2.2 tenant shape. A WSRAPI-0048
 * product-admin body (`productSlug` without `tenantSlug`) is rejected.
 */
import { apiBase } from './platform';

export interface TenantBootstrap {
  tenantSlug: string;
  tenantName: string;
  tenantStatus: string;
  /** Complete URL. Use verbatim; append only `?returnUrl=` (or `&` if a query is already present). */
  loginUrl: string;
  /** Tenant AUTH origin (scheme+host, no path). Never the platform auth host. */
  authOrigin: string;
  brandOrigin?: string;
  assetOrigin?: string;
  payOrigin?: string;
  product?: { slug: string; displayName: string };
  deployEnv?: string;
}

export class BootstrapError extends Error {
  readonly status: number;

  constructor(status: number, message = 'Bootstrap failed') {
    super(message);
    this.name = 'BootstrapError';
    this.status = status;
  }
}

let current: TenantBootstrap | null = null;

export function setBootstrap(payload: TenantBootstrap): void {
  current = payload;
}

export function getBootstrap(): TenantBootstrap {
  if (!current) {
    throw new Error('Bootstrap has not been resolved');
  }
  return current;
}

/** Test seam — clears the module store so a suite can drive fetch itself. */
export function __resetBootstrap(): void {
  current = null;
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parseTenantBody(body: unknown, httpStatus: number): TenantBootstrap {
  if (body === null || typeof body !== 'object') {
    throw new BootstrapError(httpStatus);
  }
  const rec = body as Record<string, unknown>;

  const tenantSlug = nonEmptyString(rec.tenantSlug);
  // WSRAPI-0048 product-admin shape: productSlug present, no tenantSlug.
  if (nonEmptyString(rec.productSlug) && !tenantSlug) {
    throw new BootstrapError(httpStatus);
  }

  const tenantName = nonEmptyString(rec.tenantName);
  const tenantStatus = nonEmptyString(rec.tenantStatus);
  const loginUrl = nonEmptyString(rec.loginUrl);
  const authOriginRaw = nonEmptyString(rec.authOrigin);

  if (!tenantSlug || !tenantName || !tenantStatus || !loginUrl || !authOriginRaw) {
    throw new BootstrapError(httpStatus);
  }

  const result: TenantBootstrap = {
    tenantSlug,
    tenantName,
    tenantStatus,
    loginUrl,
    authOrigin: authOriginRaw.replace(/\/$/, ''),
  };

  const brandOrigin = nonEmptyString(rec.brandOrigin);
  if (brandOrigin) result.brandOrigin = brandOrigin;
  const assetOrigin = nonEmptyString(rec.assetOrigin);
  if (assetOrigin) result.assetOrigin = assetOrigin;
  const payOrigin = nonEmptyString(rec.payOrigin);
  if (payOrigin) result.payOrigin = payOrigin;
  const deployEnv = nonEmptyString(rec.deployEnv);
  if (deployEnv) result.deployEnv = deployEnv;

  if (rec.product && typeof rec.product === 'object') {
    const product = rec.product as Record<string, unknown>;
    const slug = nonEmptyString(product.slug);
    const displayName = nonEmptyString(product.displayName);
    if (slug && displayName) {
      result.product = { slug, displayName };
    }
  }

  return result;
}

export async function fetchBootstrap(): Promise<TenantBootstrap> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}/bootstrap`);
  } catch {
    throw new BootstrapError(0);
  }
  if (!res.ok) {
    throw new BootstrapError(res.status);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new BootstrapError(res.status);
  }

  return parseTenantBody(body, res.status);
}
